/**
 * Dawson 21 Photo Series - Industry-standard dental photography protocol
 *
 * Defines all 21 photo categories used in comprehensive dental documentation.
 * Each category includes metadata for photo prompts, guides, and validation.
 */

export const DAWSON_CATEGORIES = {
  // ===== EXTRAORAL PHOTOS (5) =====

  FRONTAL_REPOSE: {
    id: 'frontal_repose',
    name: 'Frontal - Repose',
    description: 'Full face, lips at rest, eyes open',
    type: 'extraoral',
    sampleImageUrl: '/guides/frontal-repose.jpg',
    order: 1,
    instructions: [
      'Patient facing camera directly',
      'Lips relaxed, slightly parted',
      'Eyes open and looking at camera',
      'Neutral facial expression',
      'Frame: Full face with ears visible'
    ]
  },

  FRONTAL_SMILE: {
    id: 'frontal_smile',
    name: 'Frontal - Full Smile',
    description: 'Full face, maximum smile, showing all teeth',
    type: 'extraoral',
    sampleImageUrl: '/guides/frontal-smile.jpg',
    order: 2,
    instructions: [
      'Patient smiling naturally and broadly',
      'All teeth visible',
      'Eyes open, looking at camera',
      'Frame: Full face with ears visible',
      'Capture maximum smile width'
    ]
  },

  PROFILE_REPOSE: {
    id: 'profile_repose',
    name: 'Profile - Repose',
    description: 'Side view, lips at rest',
    type: 'extraoral',
    sampleImageUrl: '/guides/profile-repose.jpg',
    order: 3,
    instructions: [
      'True 90-degree side profile',
      'Lips relaxed, at rest',
      'Eyes open, looking straight ahead',
      'Frame: Full side of face from ear to chin',
      'Capture facial profile line'
    ]
  },

  PROFILE_SMILE: {
    id: 'profile_smile',
    name: 'Profile - Smile',
    description: 'Side view, maximum smile',
    type: 'extraoral',
    sampleImageUrl: '/guides/profile-smile.jpg',
    order: 4,
    instructions: [
      'True 90-degree side profile',
      'Patient smiling naturally',
      'Eyes open, looking straight ahead',
      'Frame: Full side of face',
      'Capture lip line and smile arc'
    ]
  },

  THREE_QUARTER: {
    id: 'three_quarter',
    name: '3/4 View - Smile',
    description: '45-degree angle, smiling',
    type: 'extraoral',
    sampleImageUrl: '/guides/three-quarter.jpg',
    order: 5,
    instructions: [
      'Patient at 45-degree angle to camera',
      'Smiling naturally',
      'Shows both front and side features',
      'Frame: Face from forehead to chin',
      'Capture dimensional smile view'
    ]
  },

  // ===== INTRAORAL RETRACTED PHOTOS (10) =====

  FRONTAL_OCCLUSION: {
    id: 'frontal_occlusion',
    name: 'Frontal - Occlusion',
    description: 'Retracted, teeth in occlusion, frontal view',
    type: 'intraoral',
    sampleImageUrl: '/guides/frontal-occlusion.jpg',
    order: 6,
    instructions: [
      'Retract lips with cheek retractors',
      'Teeth in full occlusion (bite together)',
      'Camera perpendicular to teeth',
      'Capture entire smile width',
      'Show upper and lower teeth meeting'
    ]
  },

  RIGHT_BUCCAL: {
    id: 'right_buccal',
    name: 'Right Buccal',
    description: 'Right side view, teeth in occlusion',
    type: 'intraoral',
    sampleImageUrl: '/guides/right-buccal.jpg',
    order: 7,
    instructions: [
      'Retract cheek on right side',
      'Teeth in full occlusion',
      'Capture from canine to last molar',
      'Camera at 90-degree angle to buccal surface',
      'Show occlusal relationship'
    ]
  },

  LEFT_BUCCAL: {
    id: 'left_buccal',
    name: 'Left Buccal',
    description: 'Left side view, teeth in occlusion',
    type: 'intraoral',
    sampleImageUrl: '/guides/left-buccal.jpg',
    order: 8,
    instructions: [
      'Retract cheek on left side',
      'Teeth in full occlusion',
      'Capture from canine to last molar',
      'Camera at 90-degree angle to buccal surface',
      'Show occlusal relationship'
    ]
  },

  MAXILLARY_OCCLUSAL: {
    id: 'maxillary_occlusal',
    name: 'Maxillary Occlusal',
    description: 'Top arch, mirror view from below',
    type: 'intraoral',
    sampleImageUrl: '/guides/maxillary-occlusal.jpg',
    order: 9,
    instructions: [
      'Use occlusal mirror below upper arch',
      'Capture entire upper arch',
      'Include all teeth and palate',
      'Camera angled slightly downward',
      'Mirror positioned parallel to occlusal plane'
    ]
  },

  MANDIBULAR_OCCLUSAL: {
    id: 'mandibular_occlusal',
    name: 'Mandibular Occlusal',
    description: 'Bottom arch, mirror view from above',
    type: 'intraoral',
    sampleImageUrl: '/guides/mandibular-occlusal.jpg',
    order: 10,
    instructions: [
      'Use occlusal mirror above lower arch',
      'Capture entire lower arch',
      'Include all teeth and tongue area',
      'Camera angled slightly upward',
      'Mirror positioned parallel to occlusal plane'
    ]
  },

  ANTERIOR_RETRACTED: {
    id: 'anterior_retracted',
    name: 'Anterior - Retracted',
    description: 'Front teeth, retracted view',
    type: 'intraoral',
    sampleImageUrl: '/guides/anterior-retracted.jpg',
    order: 11,
    instructions: [
      'Retract lips fully',
      'Teeth slightly apart',
      'Focus on upper and lower front 6 teeth',
      'Capture both arches',
      'Show incisal edges clearly'
    ]
  },

  RIGHT_LATERAL: {
    id: 'right_lateral',
    name: 'Right Lateral',
    description: 'Right canine relationship',
    type: 'intraoral',
    sampleImageUrl: '/guides/right-lateral.jpg',
    order: 12,
    instructions: [
      'Retract right cheek',
      'Teeth in occlusion',
      'Focus on canine relationship',
      'Capture upper and lower canines meeting',
      'Show anterior guidance'
    ]
  },

  LEFT_LATERAL: {
    id: 'left_lateral',
    name: 'Left Lateral',
    description: 'Left canine relationship',
    type: 'intraoral',
    sampleImageUrl: '/guides/left-lateral.jpg',
    order: 13,
    instructions: [
      'Retract left cheek',
      'Teeth in occlusion',
      'Focus on canine relationship',
      'Capture upper and lower canines meeting',
      'Show anterior guidance'
    ]
  },

  OVERJET: {
    id: 'overjet',
    name: 'Overjet',
    description: 'Horizontal overlap of front teeth',
    type: 'intraoral',
    sampleImageUrl: '/guides/overjet.jpg',
    order: 14,
    instructions: [
      'Side view of front teeth',
      'Teeth in occlusion',
      'Capture horizontal overlap',
      'Focus on incisal edge relationship',
      'Show any spacing or protrusion'
    ]
  },

  OVERBITE: {
    id: 'overbite',
    name: 'Overbite',
    description: 'Vertical overlap of front teeth',
    type: 'intraoral',
    sampleImageUrl: '/guides/overbite.jpg',
    order: 15,
    instructions: [
      'Frontal view of front teeth',
      'Teeth in occlusion',
      'Capture vertical overlap',
      'Show how much upper teeth cover lowers',
      'Focus on incisal edge coverage'
    ]
  },

  // ===== CLOSEUP DETAIL PHOTOS (6) =====

  UPPER_ANTERIOR: {
    id: 'upper_anterior',
    name: 'Upper Anterior Closeup',
    description: 'Upper front 6 teeth, detail view',
    type: 'closeup',
    sampleImageUrl: '/guides/upper-anterior.jpg',
    order: 16,
    instructions: [
      'Focus on upper front 6 teeth (canine to canine)',
      'Capture labial surface detail',
      'Show gingival margins',
      'Capture any restorations or defects',
      'Fill frame with anterior teeth'
    ]
  },

  LOWER_ANTERIOR: {
    id: 'lower_anterior',
    name: 'Lower Anterior Closeup',
    description: 'Lower front 6 teeth, detail view',
    type: 'closeup',
    sampleImageUrl: '/guides/lower-anterior.jpg',
    order: 17,
    instructions: [
      'Focus on lower front 6 teeth (canine to canine)',
      'Capture labial surface detail',
      'Show gingival margins',
      'Capture any restorations or defects',
      'Fill frame with anterior teeth'
    ]
  },

  RIGHT_POSTERIOR: {
    id: 'right_posterior',
    name: 'Right Posterior',
    description: 'Right back teeth, detail view',
    type: 'closeup',
    sampleImageUrl: '/guides/right-posterior.jpg',
    order: 18,
    instructions: [
      'Focus on right premolars and molars',
      'Capture buccal surfaces',
      'Show occlusal surfaces if possible',
      'Include gingival condition',
      'Capture any restorations or pathology'
    ]
  },

  LEFT_POSTERIOR: {
    id: 'left_posterior',
    name: 'Left Posterior',
    description: 'Left back teeth, detail view',
    type: 'closeup',
    sampleImageUrl: '/guides/left-posterior.jpg',
    order: 19,
    instructions: [
      'Focus on left premolars and molars',
      'Capture buccal surfaces',
      'Show occlusal surfaces if possible',
      'Include gingival condition',
      'Capture any restorations or pathology'
    ]
  },

  PALATAL: {
    id: 'palatal',
    name: 'Palatal View',
    description: 'Upper arch from palatal side',
    type: 'closeup',
    sampleImageUrl: '/guides/palatal.jpg',
    order: 20,
    instructions: [
      'Use mirror to capture palatal surfaces',
      'Focus on upper anterior teeth from inside',
      'Show palatal gingiva',
      'Capture any palatal restorations',
      'Include palatal contours'
    ]
  },

  LINGUAL: {
    id: 'lingual',
    name: 'Lingual View',
    description: 'Lower arch from tongue side',
    type: 'closeup',
    sampleImageUrl: '/guides/lingual.jpg',
    order: 21,
    instructions: [
      'Use mirror to capture lingual surfaces',
      'Focus on lower anterior teeth from inside',
      'Show lingual gingiva',
      'Capture any lingual restorations',
      'Include lingual contours'
    ]
  }
};

// Helper functions

/**
 * Get all photo categories sorted by order
 */
export const getAllCategories = () => {
  return Object.values(DAWSON_CATEGORIES).sort((a, b) => a.order - b.order);
};

/**
 * Get categories by type
 * @param {string} type - 'extraoral', 'intraoral', or 'closeup'
 */
export const getCategoriesByType = (type) => {
  return Object.values(DAWSON_CATEGORIES)
    .filter(cat => cat.type === type)
    .sort((a, b) => a.order - b.order);
};

/**
 * Get category by ID
 * @param {string} id - Category ID
 */
export const getCategoryById = (id) => {
  return Object.values(DAWSON_CATEGORIES).find(cat => cat.id === id);
};

/**
 * Get total number of categories
 */
export const getTotalCategories = () => {
  return Object.keys(DAWSON_CATEGORIES).length;
};

export default DAWSON_CATEGORIES;
